import { useState } from "react";
import { PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import type { UseFilterBuilderReturn } from "../hooks/useFilterBuilder";
import { containsKeywordDelimiter, parsePastedKeywords } from "../parsePastedList";
import NotificationMethodCheckboxes from "@/ui-components/NotificationMethodCheckboxes";

interface QueryBuilderPaneProps {
  builder: UseFilterBuilderReturn;
  onCancel?: () => void;
}

export default function QueryBuilderPane({ builder, onCancel }: QueryBuilderPaneProps) {
  // Chip-based keyword state
  const [keywordInput, setKeywordInput] = useState("");
  const [isAdvancedMode, setIsAdvancedMode] = useState(!!builder.searchKeyword.trim());
  const [keywordChips, setKeywordChips] = useState<string[]>(() => {
    // Parse existing searchKeyword into chips if it's a simple flat list
    const kw = builder.searchKeyword.trim();
    if (!kw) return [];
    // Try to split by OR (simple flat case)
    // Try to parse simple flat expressions (no nested grouping)
    const hasNesting = (kw.match(/\(/g) || []).length > 1;
    if (!hasNesting) {
      return kw.split(/\s+OR\s+/i).map((s) => s.trim().replace(/^[("]+|[)"]+$/g, "")).filter(Boolean);
    }
    // Complex expression — start in advanced mode
    return [];
  });
  const [chipOperator, setChipOperator] = useState<"OR" | "AND">("OR");

  // Sync chips to builder.searchKeyword
  const syncChipsToKeyword = (chips: string[], op: "OR" | "AND") => {
    if (chips.length === 0) {
      builder.setSearchKeyword("");
      return;
    }
    const joined = chips
      .map((c) => (c.includes(" ") ? `(${c})` : c))
      .join(` ${op} `);
    builder.setSearchKeyword(joined);
  };

  const addChip = () => {
    const trimmed = keywordInput.trim();
    if (trimmed && !keywordChips.includes(trimmed)) {
      const next = [...keywordChips, trimmed];
      setKeywordChips(next);
      syncChipsToKeyword(next, chipOperator);
      setKeywordInput("");
    }
  };

  const addChipsBulk = (values: string[]) => {
    if (values.length === 0) return;
    const seen = new Set(keywordChips);
    const next = [...keywordChips];
    for (const v of values) {
      if (!seen.has(v)) {
        seen.add(v);
        next.push(v);
      }
    }
    setKeywordChips(next);
    syncChipsToKeyword(next, chipOperator);
    setKeywordInput("");
  };

  const removeChip = (chip: string) => {
    const next = keywordChips.filter((c) => c !== chip);
    setKeywordChips(next);
    syncChipsToKeyword(next, chipOperator);
  };

  const toggleChipOperator = () => {
    const next = chipOperator === "OR" ? "AND" : "OR";
    setChipOperator(next);
    syncChipsToKeyword(keywordChips, next);
  };

  return (
    <div className="h-full flex flex-col border-l border-gray-700/50 bg-gray-900/50 overflow-hidden">
      {/* Header — name input only */}
      <div className="px-4 py-3 border-b border-gray-700/30">
        <input
          type="text"
          value={builder.queryName}
          onChange={(e) => builder.setQueryName(e.target.value)}
          placeholder="Filter name"
          className="w-full px-3 py-2.5 text-sm bg-gray-800 text-gray-100 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue placeholder:text-gray-500"
        />

        {builder.formErrors.categories && (
          <div className="mt-1.5 rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
            {builder.formErrors.categories}
          </div>
        )}
        {builder.formErrors.keywords && (
          <div className="mt-1.5 rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
            {builder.formErrors.keywords}
          </div>
        )}
      </div>

      {/* Body (scrollable) */}
      <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent px-4 py-4 space-y-5">
        {/* Query preview */}
        <div>
          <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">
            Query
          </div>
          <QueryPreview builder={builder} />
        </div>

        {/* Keyword input — chip mode or advanced textarea */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wide">
              Additional Keywords
            </div>
            <button
              onClick={() => setIsAdvancedMode(!isAdvancedMode)}
              className="text-2xs text-squawk-blue hover:text-squawk-blue/80 transition-colors"
            >
              {isAdvancedMode ? "Simple mode" : "Advanced mode"}
            </button>
          </div>

          {isAdvancedMode ? (
            <>
              <textarea
                value={builder.searchKeyword}
                onChange={(e) => builder.setSearchKeyword(e.target.value)}
                placeholder='(trump AND powell) OR (iran AND strait) OR tariffs'
                className="w-full px-3 py-2.5 text-sm bg-gray-800 text-gray-100 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue placeholder:text-gray-500 min-h-[60px] resize-y"
                rows={2}
              />
              <div className="text-2xs text-gray-500 mt-1.5">
                Use parentheses to group terms, <code className="bg-gray-700 px-1 rounded text-gray-300">OR</code> to match any,{" "}
                <code className="bg-gray-700 px-1 rounded text-gray-300">AND</code> to require all.
              </div>
            </>
          ) : (
            <>
              <div className="flex gap-1.5">
                <input
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addChip(); } }}
                  onPaste={(e) => {
                    const text = e.clipboardData.getData("text");
                    if (!containsKeywordDelimiter(text)) return;
                    e.preventDefault();
                    addChipsBulk(parsePastedKeywords(text));
                  }}
                  placeholder="Add keyword..."
                  className="flex-1 px-3 py-1.5 text-sm bg-gray-800 text-gray-100 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue placeholder:text-gray-600"
                />
                <button onClick={addChip} className="px-2 py-1.5 text-xs rounded bg-gray-700 text-gray-300 hover:bg-gray-600 transition-colors">
                  <PlusIcon className="size-3.5" />
                </button>
              </div>
              {keywordChips.length > 0 && (
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  {keywordChips.map((chip, i) => (
                    <span key={chip} className="contents">
                      {i > 0 && (
                        <button
                          onClick={toggleChipOperator}
                          className="px-1.5 py-0.5 text-2xs font-bold rounded border border-squawk-blue/50 bg-squawk-blue/20 text-squawk-blue hover:bg-squawk-blue/30 transition-colors cursor-pointer"
                          title={`Click to toggle to ${chipOperator === "OR" ? "AND" : "OR"}`}
                        >
                          {chipOperator}
                        </button>
                      )}
                      <span className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded bg-purple-900/20 text-purple-300 border border-purple-800/30 text-xs">
                        {chip}
                        <button onClick={() => removeChip(chip)} className="p-0.5 text-purple-400 hover:text-purple-200">
                          <XMarkIcon className="size-2.5" />
                        </button>
                      </span>
                    </span>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Keyword size */}
        {builder.hasKeywords && (
          <div className={clsx(
            "rounded border px-3 py-2 text-xs",
            builder.keywordsSize.ok
              ? "border-gray-700 text-gray-400"
              : "border-red-500/40 bg-red-500/10 text-red-300"
          )}>
            {builder.keywordsSize.count}/{builder.MAX_KEYWORDS_COUNT} keywords
            {" · "}
            {builder.keywordsSize.totalChars}/{builder.MAX_KEYWORDS_TOTAL_CHARS} chars
          </div>
        )}

        {/* Notifications */}
        <div>
          <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">
            Notifications
          </div>
          <NotificationMethodCheckboxes
            value={builder.notifications}
            onChange={builder.setNotifications}
            comingSoonMethods={["sound", "popup"]}
            variant="pane"
          />
        </div>
      </div>

      {/* Footer */}
      <div className={clsx(
        "px-4 py-3 border-t border-gray-700/30 flex flex-col sm:flex-row sm:items-center gap-2",
        builder.isEditing ? "sm:justify-between" : "sm:justify-end"
      )}>
        {builder.isEditing && (
          <button
            onClick={builder.handleDelete}
            disabled={builder.isDeleting}
            className="w-full sm:w-auto px-4 py-2 text-sm font-medium rounded border border-red-800/50 text-red-400 hover:bg-red-900/20 hover:text-red-300 transition-colors disabled:opacity-50 order-3 sm:order-1"
          >
            {builder.isDeleting ? "Deleting..." : "Delete"}
          </button>
        )}
        <div className="flex flex-col sm:flex-row gap-2 order-1 sm:order-2">
          {onCancel && (
            <button
              onClick={onCancel}
              className="w-full sm:w-auto px-4 py-2 text-sm font-medium rounded bg-gray-700/50 border border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-gray-100 transition-colors"
            >
              Cancel
            </button>
          )}
          <button
            onClick={builder.handleSubmit}
            disabled={builder.isSubmitting || !builder.queryName.trim()}
            className="w-full sm:w-auto px-4 py-2 text-sm font-medium rounded bg-squawk-blue text-white hover:bg-squawk-blue/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {builder.isSubmitting ? "Saving..." : builder.isEditing ? "Save Changes" : "Save Filter"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Query Preview with visual grouping ─────────────────────────────────

function QueryPreview({ builder }: { builder: UseFilterBuilderReturn }) {
  const hasAnything = builder.hasKeywords || builder.hasWatchlistsOrTags;

  if (!hasAnything) {
    return (
      <div className="bg-gray-800/50 rounded px-3 py-4 text-sm text-gray-500">
        Select items from the sidebar to build your query
      </div>
    );
  }

  return (
    <div className="bg-gray-800/50 rounded px-3 py-3 space-y-2">
      {/* Keyword group */}
      {builder.hasKeywords && (
        <div className="border border-purple-800/30 rounded px-2 py-2">
          <div className="text-2xs font-medium text-purple-400/70 uppercase tracking-wider mb-1.5">Keywords</div>
          <div className="flex flex-wrap items-center gap-1.5">
            {builder.searchKeyword.trim() && (
              <QueryChip
                label={builder.searchKeyword.trim()}
                accent="border-l-purple-400"
                onRemove={() => builder.setSearchKeyword("")}
              />
            )}
            {builder.selectedGrouped.keywordOnly.map((p, i) => (
              <span key={p.id} className="contents">
                {(i > 0 || builder.searchKeyword.trim()) && (
                  <OperatorToggle value={builder.keywordWatchlistOp} onChange={builder.setKeywordWatchlistOp} />
                )}
                <QueryChip
                  label={p.name}
                  accent="border-l-purple-400"
                  onRemove={() => builder.togglePortfolio(p.id)}
                />
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Joiner between keywords and watchlists/tags */}
      {builder.hasKeywords && builder.hasWatchlistsOrTags && (
        <div className="flex justify-center py-1">
          <OperatorToggle value={builder.keywordOp} onChange={builder.setKeywordOp} />
        </div>
      )}

      {/* Ticker Watchlists group */}
      {builder.hasTickerWatchlists && (
        <div className="border border-blue-800/30 rounded px-2 py-2">
          <div className="text-2xs font-medium text-blue-400/70 uppercase tracking-wider mb-1.5">Ticker Watchlists</div>
          <div className="flex flex-wrap items-center gap-1.5">
            {builder.selectedGrouped.tickerOnly.map((p, i) => (
              <span key={p.id} className="contents">
                {i > 0 && <span className="text-xs text-gray-400 font-medium px-0.5">OR</span>}
                <QueryChip
                  label={p.name}
                  accent="border-l-blue-400"
                  onRemove={() => builder.togglePortfolio(p.id)}
                />
              </span>
            ))}
          </div>
        </div>
      )}

      {/* AND/OR between tickers and tags */}
      {builder.hasTickerWatchlists && builder.hasTags && (
        <div className="flex justify-center py-1">
          <OperatorToggle value={builder.filterMode} onChange={builder.setFilterMode} />
        </div>
      )}

      {/* Tags group */}
      {builder.hasTags && (
        <div className="border border-green-800/30 rounded px-2 py-2">
          <div className="text-2xs font-medium text-green-400/70 uppercase tracking-wider mb-1.5">Tags</div>
          <div className="flex flex-wrap items-center gap-1.5">
            {builder.selectedCategories.map((c, i) => (
              <span key={`c-${c.id}`} className="contents">
                {i > 0 && <span className="text-xs text-gray-400 font-medium px-0.5">OR</span>}
                <QueryChip
                  label={c.title}
                  accent="border-l-green-400"
                  onRemove={() => builder.toggleCategory(c)}
                />
              </span>
            ))}

            {builder.selectedTagWatchlistObjects.map((tw, i) => (
              <span key={`tw-${tw.id}`} className="contents">
                {(i > 0 || builder.selectedCategories.length > 0) && (
                  <span className="text-xs text-gray-400 font-medium px-0.5">OR</span>
                )}
                <QueryChip
                  label={tw.name}
                  accent="border-l-green-400"
                  onRemove={() => builder.toggleTagWatchlist(tw.id)}
                />
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Query Chip ──────────────────────────────────────────────────────────

function QueryChip({
  label,
  accent,
  onRemove,
}: {
  label: string;
  accent: string;
  onRemove: () => void;
}) {
  return (
    <span className={`inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1 rounded-r bg-gray-700/60 border border-gray-600/40 border-l-2 ${accent} text-xs text-gray-100`}>
      <span>{label}</span>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="p-0.5 text-gray-400 hover:text-gray-200 transition-colors"
      >
        <XMarkIcon className="size-3" />
      </button>
    </span>
  );
}

// ── Operator Toggle ─────────────────────────────────────────────────────

function OperatorToggle({
  value,
  onChange,
}: {
  value: "AND" | "OR";
  onChange: (v: "AND" | "OR") => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(value === "AND" ? "OR" : "AND")}
      className="px-2 py-0.5 text-2xs font-bold rounded border border-squawk-blue/50 bg-squawk-blue/20 text-squawk-blue hover:bg-squawk-blue/30 transition-colors cursor-pointer whitespace-nowrap focus:outline-none"
      title={`Click to toggle to ${value === "AND" ? "OR" : "AND"}`}
    >
      {value}&#x25BE;
    </button>
  );
}
